
import { useState, useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

interface UsePullToRefreshOptions {
  onRefresh?: () => Promise<void> | void;
  threshold?: number;
  enabled?: boolean;
  refreshQueries?: string[];
}

export function usePullToRefresh({
  onRefresh,
  threshold = 80,
  enabled = true,
  refreshQueries = []
}: UsePullToRefreshOptions = {}) {
  const queryClient = useQueryClient();
  const [isPulling, setIsPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [canPull, setCanPull] = useState(false);
  
  const startY = useRef(0);
  const currentY = useRef(0);
  const isDragging = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleRefresh = useCallback(async () => {
    if (isRefreshing) return;
    
    setIsRefreshing(true);
    
    try {
      // Haptic feedback
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.impactOccurred('medium');
      } else if (navigator.vibrate) {
        navigator.vibrate(100);
      }

      // Custom refresh function
      if (onRefresh) {
        await onRefresh();
      }

      // Refresh specified queries
      if (refreshQueries.length > 0) {
        await Promise.all(
          refreshQueries.map(queryKey => 
            queryClient.invalidateQueries({ queryKey: [queryKey] })
          )
        );
      }

      // Small delay for better UX
      await new Promise(resolve => setTimeout(resolve, 500));
    } catch (error) {
      console.error('[PULL_TO_REFRESH] Error during refresh:', error);
    } finally {
      setIsRefreshing(false);
      setIsPulling(false);
      setPullDistance(0);
    }
  }, [isRefreshing, onRefresh, refreshQueries, queryClient]);

  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (!enabled || !containerRef.current) return;
    
    const container = containerRef.current;
    const scrollTop = container.scrollTop;
    
    // Only allow pull-to-refresh when at the top
    if (scrollTop <= 5) {
      setCanPull(true);
      startY.current = e.touches[0].clientY;
      isDragging.current = true;
    }
  }, [enabled]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!enabled || !canPull || !isDragging.current) return;
    
    currentY.current = e.touches[0].clientY;
    const deltaY = currentY.current - startY.current;
    
    if (deltaY > 0) {
      e.preventDefault();
      const distance = Math.min(deltaY * 0.5, threshold * 1.5);
      setPullDistance(distance);
      
      if (distance >= threshold && !isPulling) {
        setIsPulling(true);
        // Light haptic feedback when threshold reached
        if (window.Telegram?.WebApp?.HapticFeedback) {
          window.Telegram.WebApp.HapticFeedback.impactOccurred('light');
        }
      } else if (distance < threshold && isPulling) {
        setIsPulling(false);
      }
    }
  }, [enabled, canPull, threshold, isPulling]);

  const handleTouchEnd = useCallback(() => {
    if (!enabled || !canPull) return;
    
    isDragging.current = false;
    setCanPull(false);
    
    if (isPulling && pullDistance >= threshold) {
      handleRefresh();
    } else {
      setIsPulling(false);
      setPullDistance(0);
    }
  }, [enabled, canPull, isPulling, pullDistance, threshold, handleRefresh]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !enabled) return;

    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd);

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [enabled, handleTouchStart, handleTouchMove, handleTouchEnd]);

  // Stile per spostare il contenuto verso il basso (iOS style)
  const contentStyle = {
    transform: `translateY(${pullDistance}px)`,
    transition: isDragging.current ? 'none' : 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  };

  // Stile per l'indicator che rimane fisso in alto
  const refreshIndicatorStyle = {
    height: `${pullDistance}px`,
    opacity: Math.min(pullDistance / threshold, 1),
    transition: isDragging.current ? 'none' : 'height 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  };

  return {
    containerRef,
    isPulling,
    isRefreshing,
    pullDistance,
    contentStyle,
    refreshIndicatorStyle,
    handleRefresh
  };
}
